import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function FormFillModal({ template, onClose, onSubmitted }) {
  const { visibleSites, activeSiteId, isAllSites } = useSite();
  const needsSitePick = isAllSites && (visibleSites?.length || 0) > 1;
  const orderedSteps = useMemo(
    () => [...(template.steps || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [template],
  );
  const [siteId, setSiteId] = useState(() => {
    if (needsSitePick) return "";
    return activeSiteId && activeSiteId !== "all"
      ? activeSiteId
      : (visibleSites?.[0]?.id || "");
  });
  const [values, setValues] = useState(() => {
    const init = {};
    (template.steps || []).forEach((s) => {
      // Leave bool unset (null) so the required-check catches "user
      // never clicked Yes/No" — server treats null as blank.
      if (s.input_type === "bool") init[s.id] = null;
      else if (s.input_type === "date") init[s.id] = todayISO();
      else init[s.id] = "";
    });
    return init;
  });
  // Per-step corrective action text — only relevant when a bool step
  // is answered "No". Keyed by step_id.
  const [correctives, setCorrectives] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const setV = (id, v) => setValues((prev) => ({ ...prev, [id]: v }));
  const setC = (id, v) => setCorrectives((prev) => ({ ...prev, [id]: v }));

  const missingRequired = orderedSteps.filter((s) => {
    if (!s.required) return false;
    const v = values[s.id];
    if (s.input_type === "bool") return v === null || v === undefined;
    return v === null || v === undefined || String(v).trim() === "";
  });
  // Any bool step answered "No" needs a corrective action before submit,
  // regardless of whether the step itself is marked "required" — a
  // failed check ALWAYS needs a documented follow-up.
  const missingCorrective = orderedSteps.filter(
    (s) => s.input_type === "bool"
      && values[s.id] === false
      && !(correctives[s.id] || "").trim(),
  );

  const submit = async () => {
    if (needsSitePick && !siteId) {
      toast.error("Please pick the site this submission is for.");
      return;
    }
    if (missingRequired.length > 0) {
      toast.error(`Please complete: ${missingRequired.map((s) => s.label).join(", ")}`);
      return;
    }
    if (missingCorrective.length > 0) {
      toast.error(
        `Please add a corrective action for: ${missingCorrective.map((s) => s.label).join(", ")}`,
      );
      return;
    }
    setSaving(true);
    try {
      const answers = orderedSteps.map((s) => {
        const ans = { step_id: s.id, value: values[s.id] };
        if (s.input_type === "bool" && values[s.id] === false) {
          ans.corrective_action = (correctives[s.id] || "").trim();
        }
        return ans;
      });
      await api.post("/forms/submissions", {
        template_id: template.id,
        site_id: siteId || undefined,
        answers,
        notes,
      });
      toast.success(
        template.requires_supervisor_verification
          ? "Submitted — awaiting supervisor verification"
          : "Submitted",
      );
      onSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
      data-testid="form-fill-modal"
    >
      <div
        className="vb-card p-0 w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold">{template.name}</h2>
            {template.category && (
              <div className="text-xs opacity-70 mt-0.5">{template.category}</div>
            )}
          </div>
          <button className="vb-btn ghost" onClick={onClose} data-testid="form-fill-close">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {needsSitePick && (
            <div className="space-y-1" data-testid="form-fill-site-picker">
              <label className="text-sm font-medium">
                Site <span className="text-red-500">*</span>
              </label>
              <select
                className="vb-input w-full"
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                data-testid="form-fill-site-select"
              >
                <option value="">Choose a site…</option>
                {visibleSites.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <div className="text-xs opacity-70">
                You're on <b>All sites</b>. Pick the site this submission is for.
              </div>
            </div>
          )}
          {orderedSteps.length === 0 && (
            <div className="text-sm opacity-70">This form has no steps configured.</div>
          )}
          {orderedSteps.map((s, idx) => (
            <div key={s.id} className="space-y-1" data-testid={`form-fill-step-${idx}`}>
              <label className="text-sm font-medium">
                {s.label}
                {s.required && <span className="text-red-500 ml-0.5">*</span>}
              </label>
              {s.input_type === "bool" && (
                <div className="space-y-2" data-testid={`form-fill-input-${idx}`}>
                  <div className="flex items-center gap-2">
                    <button
                      className={`vb-chip ${values[s.id] === true ? "active" : ""}`}
                      onClick={() => setV(s.id, true)}
                      data-testid={`form-fill-bool-yes-${idx}`}
                    >
                      Yes
                    </button>
                    <button
                      className={`vb-chip ${values[s.id] === false ? "active" : ""}`}
                      onClick={() => setV(s.id, false)}
                      data-testid={`form-fill-bool-no-${idx}`}
                    >
                      No
                    </button>
                  </div>
                  {values[s.id] === false && (
                    <div
                      className="p-2 rounded-md border-l-4 bg-red-50 space-y-1"
                      style={{ borderColor: "#b91c1c" }}
                      data-testid={`form-fill-corrective-wrap-${idx}`}
                    >
                      <label className="text-xs font-semibold text-red-800">
                        Corrective action <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        rows={2}
                        className="vb-input w-full"
                        placeholder="What was wrong and what did you do about it?"
                        value={correctives[s.id] || ""}
                        onChange={(e) => setC(s.id, e.target.value)}
                        data-testid={`form-fill-corrective-${idx}`}
                      />
                    </div>
                  )}
                </div>
              )}
              {s.input_type === "date" && (
                <input
                  type="date"
                  className="vb-input w-full"
                  value={values[s.id] || ""}
                  onChange={(e) => setV(s.id, e.target.value)}
                  data-testid={`form-fill-input-${idx}`}
                />
              )}
              {s.input_type === "number" && (
                <input
                  type="number"
                  step="any"
                  className="vb-input w-full"
                  value={values[s.id] ?? ""}
                  onChange={(e) => setV(s.id, e.target.value)}
                  data-testid={`form-fill-input-${idx}`}
                />
              )}
              {s.input_type === "text" && (
                <input
                  type="text"
                  className="vb-input w-full"
                  value={values[s.id] || ""}
                  onChange={(e) => setV(s.id, e.target.value)}
                  data-testid={`form-fill-input-${idx}`}
                />
              )}
            </div>
          ))}
          <div className="pt-1">
            <label className="text-sm font-medium">Notes (optional)</label>
            <textarea
              className="vb-input w-full"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything the supervisor should know"
              data-testid="form-fill-notes"
            />
          </div>
          {template.requires_supervisor_verification && (
            <div className="text-xs opacity-70 border rounded-md p-2 bg-yellow-50">
              This form requires supervisor verification. After you submit, a supervisor will review and verify it.
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t">
          <button className="vb-btn ghost" onClick={onClose} data-testid="form-fill-cancel">
            Cancel
          </button>
          <button
            className="vb-btn primary"
            onClick={submit}
            disabled={saving}
            data-testid="form-fill-submit"
          >
            {saving ? "Submitting…" : "Submit"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
